import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BaseWrapper from "../../components/shared/BaseWrapper";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  Stack,
  CardHeader,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import { getInvoicesByContractId } from "../API/billingAPI";
import InvoicesTable from "./components/InvoicesTable";

const formatDisplayDateTime = (value) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if(isNaN(d.getTime())) return value;
  const [date] = value.split('T');

  const time = d.toLocaleTimeString('en-PH', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone:'Asia/Manila'
  });

  return `${date} ${time}`;
};

const formatPaymentType = (value) => {
  const labels = {
    ONE_TIME: "One-time payment",
    WEEKLY: "Weekly",
    MONTHLY: "Monthly",
    QUARTERLY: "Quarterly",
    "SEMI-ANNUAL": "Semi-Annual",
    ANNUAL: "Annual",
    CUSTOM: "Custom",
  };

  return labels[value] || value || "N/A";
};

function ViewContract() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const contractsViewContractKey = searchParams.get("contractsViewContract");
  const contractsViewState = useMemo(() => {
    if (!contractsViewContractKey) return null;

    try {
      const stored = localStorage.getItem(contractsViewContractKey);
      if (!stored) return null;
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }, [contractsViewContractKey]);

  const contract = location.state?.contract || contractsViewState?.contract;
  const selectedClient = location.state?.selectedClient || contractsViewState?.selectedClient;
  const source = location.state?.source || contractsViewState?.source || "manageContracts";
  const [ invoices, setInvoices ] = useState([]);
  const [ invoicesLoading, setInvoicesLoading ] = useState(false);

  const handleBackClick = () => {
    if (source === "manageClients") {
      navigate("/manage-clients", { state: { selectedClient } });
    } else if (source === "contractsView") {
      navigate("/contracts-view");
    } else {
      navigate("/manage-contracts", { state: { selectedClient } });
    }
  };

  useEffect(() => {
    const fetchContractInvoices = async () => {
      if (contract?.contract_id || contract?.id) {
        setInvoicesLoading(true);
        try {
          const contractId = contract.contract_id || contract.id;
          const invoiceData = await getInvoicesByContractId(contractId);
          setInvoices(invoiceData.items ?? []);
        } catch (error) {
          console.error('Error fetching Contract invoices:', error);
        } finally {
          setInvoicesLoading(false);
        }
      }
    }

    fetchContractInvoices();
  }, [contract])

  const handleInvoiceDeleteSuccess = (deletedInvoiceId) => {
    setInvoices(invoices.filter(inv => inv.invoice_id !== deletedInvoiceId))
  };

  const handleEditClick = () => {
    navigate(`/edit-contract/${contract?.contract_id || contract?.id}`,{
      state: {
        contract,
        selectedClient,
        source: 'viewContract',
        contractSource: source,
      }
    });
  };

  if (!contract) {
    return (
      <BaseWrapper
        titleIcon={<ReceiptLongIcon />}
        title="Billing"
        subTitle="View Contract"
        subTitleRoute={"/view-contract"}
      >
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No contract data available
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
          >
            Back to Contracts
          </Button>
        </Box>
      </BaseWrapper>
    );
  }
  return (
    <BaseWrapper
      titleIcon={<ReceiptLongIcon />}
      title="Billing"
      subTitle="View Contract"
      subTitleRoute={"/view-contract"}
    >
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
            Contract Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Contract ID: {contract?.contract_id || contract?.id || 'N/A'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEditClick}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
          >
            Back
          </Button>
          
        </Stack>

        {/* Basic Information Section */}
        <Box sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Typography
                variant='h6'
                sx={{
                  paddingBottom: '0.5rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  fontSize: '1rem',
                }}
              >
                Basic Information
              </Typography>
            }
            sx={{ pb: 0, pt: 0 }}
          />
        </Box>
        <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f9f9f9', borderRadius: 2, mb: 3}}>
          <Grid container spacing={3}>
            {/* Row 1: Client ID and Name */}
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Client ID
                </Typography>
                <Typography variant="body1">
                  {contract?.client_id || 'N/A'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Client Name
                </Typography>
                <Typography variant="body1">
                  {contract?.client_name || 'N/A'}
                </Typography>
              </Box>
            </Grid>
            {/* Row 2: Contract Start and Contract End */}
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Contract Start
                </Typography>
                <Typography variant="body1">
                  {contract?.contract_start || 'N/A'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Contract End
                </Typography>
                <Typography variant="body1">
                  {contract?.contract_end || 'N/A'}
                </Typography>
              </Box>
            </Grid>

            {/* Row 3: Currency and Price */}
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Currency
                </Typography>
                <Typography variant="body1">
                  {contract?.currency || 'N/A'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Price
                </Typography>
                <Typography variant="body1">
                  {(contract?.price_minor / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0'}
                </Typography>
              </Box>
            </Grid>

            {/* Row 4: STATUS */}
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Status
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    display: 'inline-block',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: contract?.status === 'ACTIVE' ? '#d4edda' : '#f8d7da',
                    color: contract?.status === 'ACTIVE' ? '#155724' : '#721c24',
                    fontWeight: 'bold'
                  }}
                >
                  {contract?.status || 'N/A'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Payment Terms Section */}
        <Box sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Typography
                variant='h6'
                sx={{
                  paddingBottom: '0.5rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  fontSize: '1rem',
                }}
              >
                Payment Terms
              </Typography>
            }
            sx={{ pb: 0, pt: 0 }}
          />
        </Box>
        <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f9f9f9', borderRadius: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Type
                </Typography>
                <Typography variant='body2'>
                  {formatPaymentType(contract?.payment_terms?.type || contract?.["payment terms"]?.type)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Count
                </Typography>
                <Typography variant='body2'>
                  {contract?.payment_terms?.count || contract?.["payment terms"]?.count || 'N/A'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Notes
                </Typography>
                <Typography variant='body2'>
                  {contract?.payment_terms?.notes || contract?.["payment terms"]?.notes || 'N/A'}
                </Typography> 
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Metadata Section */}
        <Box sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Typography
                variant='h6'
                sx={{
                  paddingBottom: '0.5rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  fontSize: '1rem',
                }}
              >
                Metadata
              </Typography>
            }
            sx={{ pb: 0, pt: 0 }}
          />
        </Box>
        <Paper elevation={1} sx={{ p: 3, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Created On
                </Typography>
                <Typography variant="body2">
                  {formatDisplayDateTime(contract?.timestamp_create)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Updated On
                </Typography>
                <Typography variant="body2">
                  {formatDisplayDateTime(contract?.timestamp_update)}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Created By
                </Typography>
                <Typography variant="body2">
                  {contract?.user_create?.name || contract?.user_create || 'N/A'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>
                  Updated By
                </Typography>
                <Typography variant="body2">
                  {contract?.user_update?.name || contract?.user_update || 'N/A'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Invoices Section */}
        <Box sx={{ my: 3 }}>
          <InvoicesTable
            source="viewContract"
            contractSource={source}
            selectedClient={selectedClient}
            selectedContract={contract}
            invoices={invoices}
            loading={invoicesLoading}
            onDeleteSuccess={handleInvoiceDeleteSuccess}
            onInvoicesFetch={(fetched) => setInvoices(fetched)}
          />
        </Box>
      </Box>
    </BaseWrapper >
  );
}

export default ViewContract;
